import { useCallback, useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import useMatchPath from '@polyai/common/hooks/useMatchPath';
import { Role } from '@polyai/common/types/resources/userManagement';

import api from 'api';
import { useIsFeatureOn } from 'hooks/useFeatureSwitch/useIsFeatureOn';
import useSampleProject from 'hooks/useSampleProjects/useSampleProject';
import { AGENT_WIZARD_PROJECT_PATH } from 'screens/AgentWizard/paths';
import { type ProjectResource, SAMPLE_PROJECTS_RESOURCES } from './constants';

const ACCOUNT_ROUTE = '/:accountId';
const ACCOUNT_PROJECT_ROUTE = '/:accountId/:projectId';

// Root segments that belong to non-project routes (see routesRegistry.ts).
// Without this guard, URLs like /settings/admin are matched as
// /:accountId/:projectId and trigger a spurious 403 on
// accounts/settings/projects/admin/features.
const RESERVED_ROOT_SEGMENTS = new Set([
  'settings',
  'integrations',
  'logout',
  'callback',
  'oauth-redirect',
  'ot-login',
  'ot-callback',
  'ot-select-region',
]);

export const useProjectResources = () => {
  const matchPath = useMatchPath();
  const { inSampleProject } = useSampleProject();
  const { isFeatureOn } = useIsFeatureOn();

  const { accountId, projectId } = useMemo(() => {
    let match = matchPath(AGENT_WIZARD_PROJECT_PATH, false);

    if (!match) {
      match = matchPath(ACCOUNT_PROJECT_ROUTE, false);
    }

    if (!match) {
      match = matchPath(ACCOUNT_ROUTE, false);
    }

    const matchedAccountId = match?.params?.accountId;
    if (matchedAccountId && RESERVED_ROOT_SEGMENTS.has(matchedAccountId)) {
      return { accountId: undefined, projectId: undefined };
    }

    return {
      accountId: matchedAccountId,
      projectId: match?.params?.projectId,
    };
  }, [matchPath]);

  const queryKey = [accountId, projectId, 'enabledFeature'];

  const { data, isLoading, isError } = useQuery(
    queryKey,
    () => api.getEnabledFeatures(accountId!, projectId),
    {
      enabled: !!accountId && !inSampleProject,
      retry: 3,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    },
  );

  const isProjectReadOnly = useMemo(() => {
    return data?.role === Role.VIEWER;
  }, [data?.role]);

  // ============================================================================
  // V2 Permission System Logic
  // ============================================================================
  // V2 introduces per-resource read/write permissions via read_features and
  // write_features arrays. When enabled, these replace the project-level role
  // for determining resource access.

  // Check if V2 is available: feature flag must be on AND response must have V2 fields
  const isV2Available = useMemo(() => {
    const featureFlagEnabled = isFeatureOn('user_management_v2');
    const hasV2Fields =
      (data?.read_features !== undefined &&
        Array.isArray(data.read_features) &&
        data.read_features.length > 0) ||
      (data?.write_features !== undefined &&
        Array.isArray(data.write_features) &&
        data.write_features.length > 0);
    return featureFlagEnabled && hasV2Fields;
  }, [data?.read_features, data?.write_features, isFeatureOn]);

  // Extract V2 permission arrays (only when V2 is available)
  const readFeatures = useMemo(
    () => (isV2Available ? (data?.read_features ?? []) : undefined),
    [isV2Available, data?.read_features],
  );

  const writeFeatures = useMemo(
    () => (isV2Available ? (data?.write_features ?? []) : undefined),
    [isV2Available, data?.write_features],
  );

  // Calculate enabled features list
  // Priority: Sample projects > V2 combined features > Legacy data?.features
  const features = useMemo(() => {
    // 1. Sample projects always use predefined resources
    if (inSampleProject) {
      return SAMPLE_PROJECTS_RESOURCES;
    }

    // 2. If V2 is available and has permission data, combine read + write features
    if (isV2Available) {
      const hasRead = readFeatures && readFeatures.length > 0;
      const hasWrite = writeFeatures && writeFeatures.length > 0;
      if (hasRead || hasWrite) {
        return [...(readFeatures ?? []), ...(writeFeatures ?? [])];
      }
    }

    // 3. Fallback to legacy features array
    return data?.features ?? [];
  }, [
    inSampleProject,
    isV2Available,
    readFeatures,
    writeFeatures,
    data?.features,
  ]);

  // Check if a specific resource is read-only
  // Uses V2 permissions if available, otherwise falls back to project-level role
  const isResourceReadOnly = useCallback(
    (resourceName: string): boolean => {
      // V2 not available: use project-level read-only status
      if (!isV2Available) {
        return isProjectReadOnly;
      }

      // V2 available: check resource-specific permissions
      // Write access = not read-only
      if (writeFeatures?.includes(resourceName as ProjectResource)) {
        return false;
      }

      // Read-only access = read-only
      if (readFeatures?.includes(resourceName as ProjectResource)) {
        return true;
      }

      // Resource not in V2 lists: fall back to project-level behavior
      return isProjectReadOnly;
    },
    [isV2Available, writeFeatures, readFeatures, isProjectReadOnly],
  );

  return useMemo(
    () => ({
      hasFetchedProjectFeatures: !isLoading && !!data,
      features,
      restrictedFeatures: data?.restricted_features ?? [],
      isProjectReadOnly,
      readFeatures,
      writeFeatures,
      isResourceReadOnly,
      isFetchingProjectFeatures: isLoading && !!accountId && !inSampleProject,
      didProjectFeaturesError: isError,
    }),
    [
      isLoading,
      data,
      features,
      isProjectReadOnly,
      readFeatures,
      writeFeatures,
      isResourceReadOnly,
      accountId,
      isError,
      inSampleProject,
    ],
  );
};
